import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { PermissionsService } from '../services/permissions.service';

/**
 * Route guard that checks studio permissions before activating a route.
 *
 * Usage in app-routing.module.ts:
 *   { path: 'students-list', component: StudentsListComponent,
 *     canActivate: [PermissionGuard], data: { permission: 'students_view' } }
 *
 * Add `adminOnly: true` to restrict a route to administrators (super-admin
 * uids or the Drupal `administrator` role) on top of its permission check.
 *
 * If the user has no studio role assignments, the guard passes (gradual rollout).
 * If the user has assignments but lacks the required permission, they are
 * redirected to the dashboard.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {

  constructor(
    private _permissionsService: PermissionsService,
    private _router: Router,
    private _snackBar: MatSnackBar,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const permission = route.data?.['permission'] as string;
    const adminOnly = route.data?.['adminOnly'] === true;

    // Admin gating is independent of studio permissions, so it is checked
    // before waiting on them to load.
    if (adminOnly && !this._permissionsService.has_access('view_admin_links')) {
      this._snackBar.open('You do not have permission to access this page.', 'Close', { duration: 4000 });
      this._router.navigate(['/dashboard']);
      return of(false);
    }

    if (!permission) {
      return of(true);
    }

    // Wait for studio permissions to be loaded before checking.
    // Timeout after 10s to prevent indefinite blocking if the API hangs.
    return this._permissionsService.studioPermissionsLoaded.pipe(
      filter(loaded => loaded === true),
      take(1),
      timeout(10000),
      map(() => {
        if (this._permissionsService.canAccess(permission)) {
          return true;
        }
        this._snackBar.open('You do not have permission to access this page.', 'Close', { duration: 4000 });
        this._router.navigate(['/dashboard']);
        return false;
      }),
      catchError(() => {
        // Timeout or unexpected error: allow access rather than blocking.
        return of(true);
      }),
    );
  }
}
